/**
 * IXD 주석 검증 - 진입 페이지. IXD 값 편집기 진입 화면과 같은 틀로, 새 파일을 올리거나
 * 이전에 올린 IXD 를 골라 새 탭에서 결과를 연다.
 *
 * 검증 API 는 저장된 report 만 받는다. 새 파일은 편집기와 같은 업로드 API 로 report 를 만든 뒤
 * 그 reportId 로 연다 — 그래서 올린 파일은 편집기의 "이전 파일 불러오기" 목록에도 남는다.
 */

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { isInvalidIxdStructureError } from "@/apis/errors";
import { uploadIxdPreview } from "@/apis/ixd-editor/route";
import type { IxdReportSummary } from "@/apis/ixd-editor/types";
import { LeftSidebar } from "@/components/app-shell";
import { IxdReportChips } from "@/components/common/ixd-report-name";
import { ExistingIxdReportPicker } from "@/components/existing-ixd-report-picker";
import { FileDropZone, SelectedFileCard } from "@/components/file-drop-zone";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import { UPLOAD_CARD_BY_MODE } from "@/constants/ixd-upload-mode";
import queries from "@/queries";
import { pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import {
  ViewSwitchGroup,
  type ViewSwitchOption,
} from "@/toggle-ui-kit/view-switch-group";
import {
  openPendingTab,
  resolvePendingTab,
  warnPopupBlocked,
} from "@/utils/pending-tab";

type EntryMode = "upload" | "existing-report";

const MODE_OPTIONS: ViewSwitchOption<EntryMode>[] = [
  { value: "upload", label: "새 파일 업로드" },
  { value: "existing-report", label: "이전 파일 불러오기" },
];

const UPLOAD_CARD = UPLOAD_CARD_BY_MODE.upload;

/** 편집기 진입 화면의 `file_uploadZone` 과 같은 높이 — 두 탭이 같은 틀을 쓴다. */
const UPLOAD_CARD_CLASS = "h-[450px]";

const buildViewUrl = (reportId: string) =>
  `/ixd-note-checklist/view?reportId=${encodeURIComponent(reportId)}`;

export const IxdNoteChecklistPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<EntryMode>("upload");
  const [ixdFile, setIxdFile] = useState<File | null>(null);
  const [selectedReport, setSelectedReport] = useState<IxdReportSummary | null>(
    null,
  );
  const isExistingReportMode = mode === "existing-report";
  const pendingTabRef = useRef<Window | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!ixdFile) throw new Error("파일을 선택해주세요.");
      const { reportId } = await uploadIxdPreview(ixdFile);
      return reportId;
    },
    onSuccess: async (reportId) => {
      // 새 report 가 생겼다 — 이전 파일 목록(이 화면·편집기 공용)을 다시 읽게 한다.
      await queryClient.invalidateQueries({
        queryKey: queries.ixdEditorReports().queryKey,
      });
      const viewUrl = buildViewUrl(reportId);
      const pendingTab = pendingTabRef.current;
      pendingTabRef.current = null;
      // 업로드는 이미 끝났다 — 탭을 못 열면 같은 탭 이동이라도 줘야 조용히 묻히지 않는다
      if (!resolvePendingTab(pendingTab, viewUrl)) {
        warnPopupBlocked("화면 열기", () => navigate(viewUrl));
      }
    },
    onError: (error) => {
      pendingTabRef.current?.close();
      pendingTabRef.current = null;
      if (isInvalidIxdStructureError(error)) pushModal(UploadFailedModal);
    },
  });

  /** 모달로 이미 안내한 오류는 인라인 문구로 중복 노출하지 않는다. */
  const showInlineError =
    !isExistingReportMode &&
    uploadMutation.isError &&
    !isInvalidIxdStructureError(uploadMutation.error);

  const handleStart = () => {
    if (isExistingReportMode) {
      if (selectedReport) {
        window.open(
          buildViewUrl(selectedReport.reportId),
          "_blank",
          "noopener",
        );
      }
      return;
    }
    // 업로드를 기다리는 동안 팝업 차단을 피하려고 클릭 시점에 탭부터 연다
    pendingTabRef.current = openPendingTab({
      title: "주석 검증 화면 준비 중...",
      message: "주석 검증 화면을 준비하고 있습니다...",
    });
    uploadMutation.mutate();
  };

  const cardHeader = (
    <div className="flex shrink-0 items-center gap-2">
      <p className="min-w-0 flex-1 body-2-normal-semibold text-label-normal">
        IXD 업로드
      </p>
      <ViewSwitchGroup<EntryMode>
        size="32px"
        className="shrink-0 p-1"
        options={MODE_OPTIONS}
        value={mode}
        onChange={setMode}
      />
    </div>
  );

  return (
    <div className="flex h-full w-full bg-white">
      <LeftSidebar />

      <main className="flex min-w-0 flex-1 flex-col items-center overflow-y-auto bg-white px-16 pb-[100px] pt-28">
        <div className="flex w-full max-w-[1000px] flex-col gap-10">
          <div className="flex flex-col gap-2">
            <h1 className="title-2-bold text-label-normal">IXD 주석 검증</h1>
            <p className="label-1-normal-regular text-label-alternative">
              IXD 의 주석이 DART 작성 지침 체크리스트를 어기는 곳을 찾습니다. 새
              탭에서 결과가 열립니다.
            </p>
          </div>

          <section className="flex flex-col gap-6">
            <div className="flex flex-col items-end gap-4">
              {isExistingReportMode ? (
                <div
                  className={`flex w-full flex-col gap-2.5 rounded-lg border-2 border-dashed border-line-normal p-4 ${UPLOAD_CARD_CLASS}`}
                >
                  {cardHeader}
                  <ExistingIxdReportPicker
                    title=""
                    description=""
                    selectedReportId={selectedReport?.reportId ?? null}
                    onSelect={(_reportId, report) => setSelectedReport(report)}
                    showSelectionIndicator
                    showMetadata={false}
                    className="min-h-0 rounded-none border-0 p-0"
                    selectedTone="blue"
                    listHeight="fill"
                  />
                  {selectedReport && (
                    <div className="flex shrink-0 flex-col gap-2">
                      <div className="h-px w-full bg-line-normal" />
                      <SelectedFileCard
                        format="xbrl"
                        onRemove={() => setSelectedReport(null)}
                      >
                        <IxdReportChips
                          periodYear={selectedReport.periodYear}
                          periodType={selectedReport.periodType}
                        />
                        <p className="label-1-normal-regular truncate text-label-normal">
                          {selectedReport.originalFilename}
                        </p>
                      </SelectedFileCard>
                    </div>
                  )}
                </div>
              ) : (
                <FileDropZone
                  accept={UPLOAD_CARD.accept}
                  hint="*.ixd 형식"
                  header={cardHeader}
                  className={UPLOAD_CARD_CLASS}
                  file={ixdFile}
                  onFileChange={setIxdFile}
                />
              )}

              {showInlineError && (
                <p
                  role="alert"
                  className="w-full label-2-regular text-status-error"
                >
                  {uploadMutation.error instanceof Error
                    ? uploadMutation.error.message
                    : "검증 화면을 준비하는 중 오류가 발생했습니다."}
                </p>
              )}

              <Button
                variant="solid"
                color="blue"
                size={40}
                leftIcon={
                  uploadMutation.isPending ? (
                    <Loader2 className="animate-spin" strokeWidth={1.8} />
                  ) : (
                    <ListChecks strokeWidth={1.8} />
                  )
                }
                disabled={
                  isExistingReportMode
                    ? !selectedReport
                    : !ixdFile || uploadMutation.isPending
                }
                onClick={handleStart}
              >
                {uploadMutation.isPending ? "준비 중..." : "검증 시작"}
              </Button>
            </div>

            <IxdUploadNotice
              accepts={UPLOAD_CARD.accept}
              showConversionGuide={UPLOAD_CARD.showConversionGuide}
            />
          </section>
        </div>
      </main>
    </div>
  );
};
